import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../services/api';

interface Filial {
    id: string;
    name: string;
    empresaId: string;
    empresa: { id: string; name: string };
}

interface FilialContextData {
    filiais: Filial[];
    filialSelecionada: Filial | null;
    isAll: boolean;
    filialId: string | 'all';
    isLoading: boolean;
    selecionarFilial: (filialId: string | 'all') => void;
}

const FilialContext = createContext<FilialContextData>({} as FilialContextData);

export function FilialProvider({ children }: { children: React.ReactNode }) {
    const [filiais, setFiliais] = useState<Filial[]>([]);
    const [filialSelecionada, setFilialSelecionada] = useState<Filial | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        async function load() {
            try {
                const data = await api.getMinhasFiliais();
                setFiliais(data);

                const salva = localStorage.getItem('filialId');
                if (salva && salva !== 'all') {
                    const encontrada = data.find(f => f.id === salva);
                    if (encontrada) setFilialSelecionada(encontrada);
                }
                else if (data.length === 1) {
                    setFilialSelecionada(data[0]);
                    localStorage.setItem('filialId', data[0].id);
                }
            } catch (error) {
                console.error('Erro ao carregar filiais:', error);
            } finally {
                setIsLoading(false);
            }
        }
        load();
    }, []);

    const selecionarFilial = useCallback((id: string | 'all') => {
        if (id === 'all') {
            setFilialSelecionada(null);
            localStorage.setItem('filialId', 'all');
        } else {
            const encontrada = filiais.find(f => f.id === id);
            if (encontrada) {
                setFilialSelecionada(encontrada);
                localStorage.setItem('filialId', id);
                api.selecionarFilial(id).catch(console.error);
            }
        }
    }, [filiais]);

    const filialId = filialSelecionada?.id ?? 'all';
    const isAll = filialId === 'all';

    return (
        <FilialContext.Provider value={{
            filiais,
            filialSelecionada,
            isAll,
            filialId,
            isLoading,
            selecionarFilial,
        }}>
            {children}
        </FilialContext.Provider>
    );
}

export function useFilial() {
    const context = useContext(FilialContext);
    if (!context) throw new Error('useFilial deve ser usado dentro de FilialProvider');
    return context;
}